// src/components/settings/shared/dialogs/PetTypeDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  DailogLabel,
  DialogLabelText,
  StyledTextField,
  StyledSwitch,
} from '../../../forms/shared/FormElements';
import FieldHelperText from '../../../forms/shared/FieldHelperText';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import { PetType } from '../../../../services/api';

interface PetTypeDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { type: string; active: boolean }) => void;
  editing: PetType | null;
}

const PetTypeDialog: React.FC<PetTypeDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
}) => {
  const [formData, setFormData] = useState({ type: '', active: true });

  useEffect(() => {
    if (open) {
      setFormData(
        editing
          ? { type: editing.type, active: editing.active ?? true }
          : { type: '', active: true }
      );
    }
  }, [open, editing]);

  const canSave = formData.type.trim().length > 0;
  const isSystem = !!editing?.isSystem;

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Pet Type' : 'Add Pet Type'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text={editing ? 'Save' : 'Save'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.75} mt={0.2}>
        <DailogLabel>
          <DialogLabelText id="pet-type-name-label">
            Pet Type Name:
          </DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="pet-type-name-input"
          inputProps={{ 'aria-labelledby': 'pet-type-name-label' }}
          value={formData.type}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, type: e.target.value })
          }
          required
          fullWidth
          disabled={isSystem}
        />
        {isSystem && (
          <FieldHelperText helperText="Standard system item - name cannot be changed. You can still deactivate it below." />
        )}
        {editing && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={1}
          >
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
              Active
            </Typography>
            <StyledSwitch
              checked={formData.active}
              onChange={(e) =>
                setFormData({ ...formData, active: e.target.checked })
              }
            />
          </Box>
        )}
      </Box>
    </DialogBase>
  );
};

export default PetTypeDialog;
